/**
 * Tool IO-size query operations - Create
 *
 * Persists per-tool-call IO byte sizes to meridian_tool_io. One row per tool
 * EXECUTION (read AND write), captured at the registry wrap seam. Inserts are
 * best-effort: a failure here must never break a chat turn, so the caller
 * fires-and-forgets and the function swallows nothing silently — it logs and
 * rethrows so the caller's `.catch` can warn.
 */

import { v4 as uuidv4 } from 'uuid';
import { getSupabaseClient } from '../../supabase-client';
import { logger } from '../../../logger';

/** One per-tool-call IO row to insert. All sizes are byte lengths. */
export interface ToolIoEventInsert {
  chat_id?: string | null;
  user_id?: string | null;
  tool_name: string;
  surface?: string | null;
  /**
   * Opaque id shared by every tool call issued by the SAME model response.
   * A different turn_id in the same chat_id is a separate inference
   * round-trip — the distinction that used to be a 300ms timing heuristic.
   */
  turn_id?: string | null;
  /** Dense 0-based ordinal of that turn within the originating request. */
  turn_index?: number | null;
  input_bytes: number;
  output_bytes: number;
  output_items?: number | null;
  result_truncated?: boolean | null;
  /**
   * Counterfactual: related sections/sources one consolidated call returned.
   * `sections_returned - 1` = the follow-up round-trips it displaced.
   */
  sections_returned?: number | null;
  /** Counterfactual: ids one batch call resolved; `ids_requested - 1` displaced. */
  ids_requested?: number | null;
  /** Counterfactual: true when served from the read dedupe cache (one displaced call). */
  cache_hit?: boolean | null;
  /**
   * Bounded failure class from a CLOSED vocabulary (not_found, validation,
   * permission, auth, rate_limited, timeout, upstream, database, unsupported,
   * too_large, unknown). A CHECK constraint rejects anything else.
   */
  error_kind?: string | null;
  /**
   * The tool's OWN structured error code, uppercased and identifier-shaped.
   * NEVER an error message: messages on these paths can quote communications
   * bodies and call content (SSNs, DOBs). A CHECK constraint enforces the shape.
   */
  error_code?: string | null;
  is_error?: boolean;
  duration_ms?: number | null;
}

/**
 * Insert one tool-IO telemetry row. No-op is not possible (single row); throws
 * on DB error so the caller can log — but the caller MUST treat a failure as
 * non-fatal (telemetry, not chat state).
 */
export async function createToolIoEvent(event: ToolIoEventInsert): Promise<void> {
  try {
    const client = getSupabaseClient();

    const { error } = await client.from('meridian_tool_io').insert({
      id: uuidv4(),
      chat_id: event.chat_id ?? null,
      user_id: event.user_id ?? null,
      tool_name: event.tool_name,
      surface: event.surface ?? null,
      turn_id: event.turn_id ?? null,
      turn_index: event.turn_index ?? null,
      input_bytes: event.input_bytes,
      output_bytes: event.output_bytes,
      output_items: event.output_items ?? null,
      result_truncated: event.result_truncated ?? null,
      sections_returned: event.sections_returned ?? null,
      ids_requested: event.ids_requested ?? null,
      cache_hit: event.cache_hit ?? null,
      error_kind: event.error_kind ?? null,
      error_code: event.error_code ?? null,
      is_error: event.is_error ?? false,
      duration_ms: event.duration_ms ?? null,
      created_at: new Date().toISOString(),
    });
    if (error) {
      throw error;
    }
  } catch (error) {
    logger.error('error creating tool io event', error, {
      op: 'createToolIoEvent',
      tool: event.tool_name,
    });
    throw error;
  }
}
